<template>
    <component :is="tag" :style="cssRules">
        <template v-if="slotRow">
            <template v-for="(item, index) in dataChunk">
                <slot :item="item" :index="index" :chunkIndex="indexes[index]"></slot>
            </template>
        </template>
        <template v-else>
            <slot :dataChunk="dataChunk" :indexes="indexes" :totalCount="totalCount" :chunkStartPos="cache.chunkStartPos" :chunkEndPos="cache.chunkEndPos"></slot>
        </template>
    </component>
</template>

<script>
const debug = false

export default {
    name: "oc-scroll-content-paginator",

    props: {
        src: { type: Array, required: true },
        height: { type: Number, default: 400 },
        scrollChunkSize: { type: Number, default: 1 },
        visibleSize: { type: Number, default: 10 },
        preserveLoaded: { type: Boolean, default: false },
        slotRow: { type: Boolean, default: false },
        tag: { type: String, default: 'div' }
    },

    data() {
        return {
            totalCount: 0,
            currentScrollPage: 0,
            dataChunk: [],
            indexes: []
        }
    },

    watch: {
        src: {
            handler(val) {
                this.totalCount = parseInt(val.length)

                var {chunkStartPos, chunkEndPos} = this.calcVisibleChunk(this.getCurrentStartRow())

                this.applyChunkPositions(chunkStartPos, chunkEndPos)
            }
        }
    },

    created(){
        this.init()

        if( this.scrollChunkSize > this.visibleSize )
        {
            throw new Error(`[ScrollContentPaginator] "scrollChunkSize" cannot be bigger than "visibleSize". Not supported.`)
        }

        if( this.scrollChunkSize < this.visibleSize && this.visibleSize % this.scrollChunkSize !== 0 )
        {
            throw new Error(`[ScrollContentPaginator] "visibleSize" should be equal to N chunks based on "scrollChunkSize".`)
        }

        this._debug('visibleSize', this.visibleSize)
        this._debug('totalCount', this.totalCount)
        this._debug('scrollChunkSize', this.scrollChunkSize)
    },

    mounted() {
        // load first chunk
        this.nextChunk(this.getScroll(), 0, true)

        this.$el.addEventListener('scroll', this.onScroll)
    },

    destroyed() {
        window.removeEventListener('scroll', this.onScroll)
    },

    computed: {
        cssRules() {
            return {
                'max-height': this.height + 'px',
                'overflow-y': 'auto'
            }
        }
    },

    methods: {
        init()
        {
            this.totalCount = parseInt(this.src.length)

            this.cache = {}
        },

        getCurrentStartRow()
        {
            return this.indexes[0]
        },

        getCurrentEndRow()
        {
            return this.indexes[this.indexes.length - 1]
        },

        goToRow(rowNum)
        {
            if( this.preserveLoaded )
            {
                throw new Error(`[ScrollContentPaginator] "goToRow()" is not supported when "preserveLoaded" is set to "true".`)
            }

            const scroll = this.getScroll()

            if( scroll === false ) {
                return
            }

            this.nextChunk(scroll, parseInt(rowNum))
        },

        onScroll(e)
        {
            const scroll = this.getScroll()

            if( scroll === false ) {
                return
            }

            // use dynamically calculated current page
            if( this.preserveLoaded )
            {
                this.addNextChunk(scroll)
                return
            }

            this.nextChunk(scroll)
        },

        emitChange() {
            this.$emit('change', {
                chunkStartPos: this.cache.chunkStartPos,
                chunkEndPos: this.cache.chunkEndPos,
                totalCount: this.totalCount
            })
        },

        addNextChunk(scroll)
        {
            this._debug('-- addNextChunk --')

            if( ! scroll.endOfScroll )
            {
                this._debug('skip - same page', this.cache)
                return
            }

            var chunkStartPos = this.cache.chunkStartPos
            var chunkEndPos = this.cache.chunkEndPos

            if( scroll.endOfScroll && chunkEndPos < this.totalCount )
            {
                this._debug('handle scroll bottom')

                chunkStartPos = chunkEndPos
                chunkEndPos = chunkStartPos + this.scrollChunkSize

                if( chunkEndPos > this.totalCount ) {
                    chunkEndPos = this.totalCount
                }
            }

            this._debug('Current: ', chunkStartPos,'-', chunkEndPos)

            if(
                this.cache.chunkStartPos === chunkStartPos &&
                this.cache.chunkEndPos === chunkEndPos
            ) {
                this._debug('skip - same page CACHE')
                return
            }

            this.cache.chunkStartPos = chunkStartPos
            this.cache.chunkEndPos = chunkEndPos

            const chunk = this.src.slice(chunkStartPos, chunkEndPos)
            this.dataChunk.push(...chunk)

            for (let i = chunkStartPos; i <= (chunkEndPos-1); i++) {
               this.indexes.push(i)
            }

            this.emitChange()
        },

        nextChunk(scroll, rowNum = undefined, isFirstCall = false)
        {
            this._debug('-- nextChunk --')

            if(
                ! isFirstCall &&
                rowNum === undefined &&
                ! scroll.endOfScroll &&
                ! scroll.startOfScroll
            ) {
                this._debug('skip - same page', this.cache)
                return
            }

            if( rowNum !== undefined )
            {
                var {chunkStartPos, chunkEndPos} = this.calcVisibleChunk(rowNum)

                this._debug('Specific PAGE: ', {chunkStartPos, chunkEndPos})

                if( rowNum > 0 )
                {
                    setTimeout(() => {
                        this.$el.scrollTop = 20
                    }, 0)
                }
            }
            else
            {
                var chunkStartPos = this.cache.chunkStartPos
                var chunkEndPos = this.cache.chunkEndPos
            }

            if( scroll.startOfScroll && rowNum === undefined )
            {
                var {chunkStartPos, chunkEndPos} = this.calcVisibleChunk(chunkStartPos - this.scrollChunkSize)

                this._debug('handle scroll top', chunkStartPos - this.scrollChunkSize, {chunkStartPos, chunkEndPos})

                if( this.cache.chunkStartPos !== chunkStartPos ) {
                    this.$el.scrollTop = 20
                }
            }

            if( scroll.endOfScroll && rowNum === undefined )
            {
                var {chunkStartPos, chunkEndPos} = this.calcVisibleChunk(chunkStartPos + this.scrollChunkSize)

                this._debug('handle scroll bottom', chunkStartPos + this.scrollChunkSize, {chunkStartPos, chunkEndPos})

                if( this.cache.chunkEndPos !== chunkEndPos ) {
                    this.$el.scrollTop = this.$el.scrollTop - 20
                }
            }

            this._debug('Current: ', chunkStartPos,'-', chunkEndPos)

            if(
                this.cache.chunkStartPos === chunkStartPos &&
                this.cache.chunkEndPos === chunkEndPos
            ) {
                this._debug('skip - same page CACHE')
                return
            }

            this.applyChunkPositions(chunkStartPos, chunkEndPos)
        },

        applyChunkPositions(chunkStartPos, chunkEndPos)
        {
            this.cache.chunkStartPos = chunkStartPos
            this.cache.chunkEndPos = chunkEndPos

            let chunk = this.src.slice(chunkStartPos, chunkEndPos)

            let indexes = []
            for (let i = chunkStartPos; i <= (chunkEndPos-1); i++) {
               indexes.push(i)
            }

            let dataChunk = []
            dataChunk.push(...chunk)

            Vue.set(this, 'indexes', indexes)
            Vue.set(this, 'dataChunk', dataChunk)

            this.emitChange()
        },

        /**
         * Make sure we start with visible count and than just add this.scrollChunkSize
         */
        calcVisibleChunk(chunkStartPos = 0, chunkEndPos = undefined)
        {
            if( chunkStartPos < 0 )
            {
                this._debug(0)
                chunkStartPos = 0
                chunkEndPos = this.scrollChunkSize
            }

            if( chunkStartPos > this.totalCount )
            {
                this._debug(1)
                return {
                    chunkStartPos: (this.totalCount - this.visibleSize),
                    chunkEndPos: this.totalCount
                }
            }

            if( this.totalCount <= this.visibleSize )
            {
                this._debug(2)
                return {
                    chunkStartPos: 0,
                    chunkEndPos: this.totalCount
                }
            }

            if( chunkEndPos === undefined )
            {
                chunkEndPos = chunkStartPos + this.scrollChunkSize
            }

            if( this.visibleSize === this.scrollChunkSize )
            {
                this._debug(3)
                return {chunkStartPos, chunkEndPos}
            }

            if( this.visibleSize > this.scrollChunkSize && (chunkEndPos - chunkStartPos) < this.visibleSize )
            {
                if ( chunkStartPos === 0 )
                {
                    this._debug(4)
                    chunkEndPos = this.visibleSize

                    if( chunkEndPos > this.totalCount ) {
                        chunkEndPos = this.totalCount
                    }
                }
                else if ( chunkStartPos > 0 )
                {
                    this._debug(5)
                    chunkEndPos = chunkEndPos + Math.abs(this.visibleSize - (chunkEndPos - chunkStartPos))

                    if( chunkEndPos > this.totalCount ) {
                        chunkEndPos = this.totalCount
                    }

                    if( (chunkEndPos - chunkStartPos) < this.visibleSize )
                    {
                        this._debug(6)
                        chunkStartPos = chunkStartPos - (this.visibleSize - (chunkEndPos - chunkStartPos))

                        if( chunkStartPos < 0 )
                        {
                            this._debug(7)
                            chunkEndPos = chunkEndPos + Math.abs(chunkStartPos)
                            chunkStartPos = 0

                            if( chunkEndPos > this.totalCount ) {
                                chunkEndPos = this.totalCount
                            }
                        }
                    }
                }
            }

            this._debug(10)

            return {chunkStartPos, chunkEndPos}
        },

        getScroll()
        {
            let el = this.$el

            if( ! el ) {
                return false
            }

            // the box height
            let elementHeight = el.clientHeight

            // the scroll bar height
            let scrollHeight = Math.round(el.scrollHeight - elementHeight)

            // if we do not have any scroll
            if( scrollHeight === 0 ) {
                return false
            }

            // position of the scroll against the scroll bar height
            let scrollTop = Math.round(el.scrollTop)

            // is the scroll reached the end point bottom
            let endOfScroll = false

            let tolerance = 4
            if( scrollHeight === scrollTop || (scrollHeight - scrollTop) <= tolerance ) {
                endOfScroll = true
            }
            //

            let startOfScroll = scrollTop === 0

            // w('scrollTop', scrollTop)
            // w('scrollHeight', scrollHeight)
            // w('startOfScroll', startOfScroll)
            // w('endOfScroll', endOfScroll)

            return {
                elementHeight,
                scrollTop,
                scrollHeight,
                startOfScroll,
                endOfScroll
            }
        },

        _debug() {
            if(debug) {
                w(...arguments)
            }
        }
    }
}
</script>
